import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Route } from 'RestAPIs/travel-distances/types';

import {
  formatSecondsToHoursMinutes,
  formatByLocale,
  fromISOString
} from 'utils/date';
import { displayNumber } from 'utils/misc';

import { getRoutes } from 'RestAPIs/travel-distances';

import { Box, TextField } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';

import { DataGrid } from 'components/shared';

import AddEditTravelDistanceDialog from './components/AddEditTravelDistanceDialog';
import TravelDistanceMapDialog from './components/TravelDistanceMapDialog';

import { useDeleteTravelDistanceMutation } from './queries';
import { getAddressAsString } from './util';

const columnProperties = [
  {
    name: 'departureAddress',
    hideable: false,
    flex: 1
  },
  {
    name: 'destinationAddress',
    flex: 1
  },
  {
    name: 'transportationMode',
    flex: 1
  },
  {
    name: 'routeCalculationType',
    flex: 1
  },
  {
    name: 'distanceInMeters',
    i18nKey: 'distance',
    maxWidth: 130,
    flex: 1
  },
  {
    name: 'travelTimeInSeconds',
    i18nKey: 'travelTime',
    maxWidth: 130,
    flex: 1
  },
  {
    name: 'lastUpdated',
    flex: 1
  }
];

const TravelDistances = () => {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState<number>(20);
  const [filter, setFilter] = useState<string>('');
  const [shouldShowAddEdit, setShouldShowAddEdit] = useState<boolean>(false);
  const [dataToEdit] = useState<Route | null>(null);
  const [routeToView, setRouteToView] = useState<Route | null>(null);
  const [shouldShowMap, setShouldShowMap] = useState<boolean>(false);

  const { data: routes = [], isLoading: isLoadingRoutes } = useQuery(
    ['travelDistances'],
    async () => {
      const { data } = await getRoutes();
      return data;
    }
  );
  const { mutate: deleteRoute } = useDeleteTravelDistanceMutation();

  const onClickDeleteRow = (idsList: GridRowId[]) => {
    deleteRoute(idsList[0] as number);
  };

  const onClickViewRow = (id: GridRowId) => {
    const route = routes.find((x) => x.id === id) as Route;
    setRouteToView(route);
    setShouldShowMap(true);
  };

  const columns = columnProperties.map((props) => {
    const { name, i18nKey, ...rest } = props;

    return {
      field: name,
      headerName: t(`travelDistances.${i18nKey ?? name}`),
      ...rest
    };
  });

  const rows = useMemo(() => {
    const filteredRoutes = (routes ?? []).filter(
      (route) =>
        getAddressAsString(route.departureAddress)
          .toLowerCase()
          .includes(filter.toLowerCase()) ||
        getAddressAsString(route.destinationAddress)
          .toLowerCase()
          .includes(filter.toLowerCase())
    );

    if (!filteredRoutes.length) {
      return [];
    }

    return filteredRoutes.map((route) => ({
      id: route.id,
      departureAddress: getAddressAsString(route.departureAddress),
      destinationAddress: getAddressAsString(route.destinationAddress),
      transportationMode: t(
        `travelDistances.transportationModes.${route.transportationMode}`
      ),
      routeCalculationType: t(
        `travelDistances.calculationTypes.${route.routeCalculationType}`
      ),
      distanceInMeters: displayNumber(route.distanceInMeters / 1000, 1),
      travelTimeInSeconds: formatSecondsToHoursMinutes(
        route.travelTimeInSeconds
      ),
      lastUpdated: formatByLocale(fromISOString(route.lastUpdated))
    }));
  }, [routes, filter]);

  return (
    <Box>
      <TextField
        label="Filter"
        value={filter}
        onChange={(e) => {
          setFilter(e.target.value);
        }}
        fullWidth
      />
      <DataGrid
        loading={isLoadingRoutes}
        columns={columns}
        rows={rows}
        pagination
        pageSize={pageSize}
        rowsPerPageOptions={[10, 20, 50]}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        onDeleteClick={onClickDeleteRow}
        onViewClick={onClickViewRow}
        confirmationTitle={t('travelDistances.confirmationTitle')}
        confirmationBody={t('travelDistances.confirmationBody')}
        addRowDialog={
          <AddEditTravelDistanceDialog
            isOpen={shouldShowAddEdit}
            onClose={() => setShouldShowAddEdit(false)}
            onSave={() => setShouldShowAddEdit(false)}
            route={dataToEdit}
          />
        }
      />
      {routeToView && (
        <TravelDistanceMapDialog
          isOpen={shouldShowMap}
          onClose={() => setShouldShowMap(false)}
          route={routeToView}
        />
      )}
    </Box>
  );
};

export default TravelDistances;
